import ProductAvailabilityChip from '@components/Product/ProductAvailabilityChip';
import { Box, Stack } from '@mui/material';
import { Check, Plus } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { ProductSearchResultItem } from '../../../../globalSearch.types';
import useProductMediaThumbnail from '../../../../hooks/useProductMediaThumbnail';
import HighlightedText from '../../../HighlightedText';
import RowActionButton, { ROW_ACTION_BUTTON_SLOT_SIZE } from '../../../RowActions/RowActionButton';
import RowActions from '../../../RowActions/RowActions';
import Thumbnail from '../../../Thumbnail';
import ResultRowBase from '../../ResultRowBase';
import { ResultRowRendererProps } from '../../resultRow.types';

const IN_ORDER_BADGE_SIZE = 16;

function ProductRow({
  item,
  query,
  isActive,
  isDisabled,
  domId,
  onSelect,
  itemActions,
}: ResultRowRendererProps<ProductSearchResultItem>) {
  const { aspectRatio, fallbackIconSize } = useProductMediaThumbnail();

  const availabilities = [...new Set(item.availabilities)];
  const isInActiveOrder = Boolean(itemActions?.isProductInActiveOrder(item));

  return (
    <ResultRowBase
      testId={`global-search-item-${item.entityType}-${item.id}`}
      isActive={isActive}
      isDisabled={isDisabled}
      domId={domId}
      onClick={onSelect}
      leading={
        <Thumbnail
          imageUrl={item.imageUrl}
          alt={item.title}
          aspectRatio={aspectRatio}
          fallbackIconSize={fallbackIconSize}
        />
      }
      title={<HighlightedText text={item.title} query={query} />}
      subtitle={item.subtitle ? <HighlightedText text={item.subtitle} query={query} /> : undefined}
      line1Trailing={
        availabilities.length > 0 && (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {availabilities.map((availability) => (
              <ProductAvailabilityChip key={availability} availability={availability} />
            ))}
          </Stack>
        )
      }
      trailing={
        !isDisabled &&
        itemActions && (
          <RowActions isPersistent={isInActiveOrder}>
            {isInActiveOrder ? (
              <Box
                sx={{
                  width: ROW_ACTION_BUTTON_SLOT_SIZE,
                  height: ROW_ACTION_BUTTON_SLOT_SIZE,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RowActionButton
                  icon={Check}
                  label={LangEnum.GLOBAL_SEARCH_IN_ORDER}
                  slotSize={IN_ORDER_BADGE_SIZE}
                  testId={`global-search-add-to-order-${item.id}`}
                  iconButton={{ color: 'accent', size: 'small', shape: 'round' }}
                  onClick={() => itemActions.onAddProductToOrder(item)}
                />
              </Box>
            ) : (
              <RowActionButton
                icon={Plus}
                label={LangEnum.GLOBAL_SEARCH_ADD_TO_ORDER}
                color="accent.light"
                testId={`global-search-add-to-order-${item.id}`}
                onClick={() => itemActions.onAddProductToOrder(item)}
              />
            )}
          </RowActions>
        )
      }
    />
  );
}

export default ProductRow;
